import { memo, type JSX } from "react";
import type { OfferData, TemplateType } from "@/components/offer/offerDocumentTypes";
import styles from "@/components/mvp/OfferThumbnail.module.css";

export type OfferThumbnailProps = {
  offerData?: OfferData;
  templateType: TemplateType;
  width: number;
  height: number;
};

function QuickThumbnail({ width, height }: Pick<OfferThumbnailProps, "width" | "height">): JSX.Element {
  return (
    <svg
      className={styles.svg}
      width={width}
      height={height}
      viewBox="0 0 220 170"
      role="img"
      aria-label="Miniatura trybu Decyzyjna"
    >
      <rect width="220" height="170" fill="#ffffff" />
      <text x="110" y="48" textAnchor="middle" fill="#C7541F" fontSize="28" fontWeight="700">
        48 000 zł
      </text>
      <line x1="32" y1="65" x2="188" y2="65" stroke="#E6DED2" strokeWidth="1" />
      <text x="34" y="86" fill="#2A251E" fontSize="10" fontWeight="600">
        Malowanie ścian
      </text>
      <text x="186" y="86" textAnchor="end" fill="#2A251E" fontSize="10" fontWeight="600">
        3 200 zł
      </text>
      <text x="34" y="105" fill="#2A251E" fontSize="10" fontWeight="600">
        Gładzie gipsowe
      </text>
      <text x="186" y="105" textAnchor="end" fill="#2A251E" fontSize="10" fontWeight="600">
        2 400 zł
      </text>
      <text x="34" y="124" fill="#2A251E" fontSize="10" fontWeight="600">
        Przygotowanie podłoża
      </text>
      <text x="186" y="124" textAnchor="end" fill="#2A251E" fontSize="10" fontWeight="600">
        700 zł
      </text>
      <rect x="30" y="140" width="160" height="1" fill="#E6DED2" />
      <text x="34" y="154" fill="#6B6257" fontSize="9">
        Materiały: po stronie wykonawcy
      </text>
      <text x="34" y="166" fill="#6B6257" fontSize="9">
        Gwarancja: 24 miesiące
      </text>
    </svg>
  );
}

function ExplainedThumbnail({ width, height }: Pick<OfferThumbnailProps, "width" | "height">): JSX.Element {
  return (
    <svg
      className={styles.svg}
      width={width}
      height={height}
      viewBox="0 0 220 170"
      role="img"
      aria-label="Miniatura trybu Przekonująca"
    >
      <rect width="220" height="170" fill="#ffffff" />
      <text x="18" y="22" fill="#6B6257" fontSize="9">
        Oferta obejmuje prace przygotowawcze,
      </text>
      <text x="18" y="35" fill="#6B6257" fontSize="9">
        malowanie oraz wykończenie ścian.
      </text>
      <rect x="18" y="48" width="184" height="19" fill="#C7541F" rx="2" />
      <text x="26" y="61" fill="#ffffff" fontSize="8" fontWeight="700">
        Pozycja
      </text>
      <text x="104" y="61" fill="#ffffff" fontSize="8" fontWeight="700">
        Ilość
      </text>
      <text x="139" y="61" fill="#ffffff" fontSize="8" fontWeight="700">
        Jedn.
      </text>
      <text x="194" y="61" textAnchor="end" fill="#ffffff" fontSize="8" fontWeight="700">
        Wartość
      </text>
      <text x="26" y="80" fill="#2A251E" fontSize="8">
        Malowanie ścian
      </text>
      <text x="111" y="80" textAnchor="middle" fill="#2A251E" fontSize="8">
        80
      </text>
      <text x="146" y="80" textAnchor="middle" fill="#2A251E" fontSize="8">
        m2
      </text>
      <text x="194" y="80" textAnchor="end" fill="#2A251E" fontSize="8">
        3 200
      </text>
      <text x="26" y="98" fill="#2A251E" fontSize="8">
        Gładzie gipsowe
      </text>
      <text x="111" y="98" textAnchor="middle" fill="#2A251E" fontSize="8">
        60
      </text>
      <text x="146" y="98" textAnchor="middle" fill="#2A251E" fontSize="8">
        m2
      </text>
      <text x="194" y="98" textAnchor="end" fill="#2A251E" fontSize="8">
        2 400
      </text>
      <text x="26" y="116" fill="#2A251E" fontSize="8">
        Przygotowanie podłoża
      </text>
      <text x="111" y="116" textAnchor="middle" fill="#2A251E" fontSize="8">
        1
      </text>
      <text x="146" y="116" textAnchor="middle" fill="#2A251E" fontSize="8">
        kpl.
      </text>
      <text x="194" y="116" textAnchor="end" fill="#2A251E" fontSize="8">
        700
      </text>
      <rect x="18" y="124" width="184" height="18" fill="#F6F1EA" rx="2" />
      <text x="26" y="136" fill="#2A251E" fontSize="8" fontWeight="700">
        RAZEM
      </text>
      <text x="194" y="136" textAnchor="end" fill="#C7541F" fontSize="9" fontWeight="700">
        48 000 zł
      </text>
      <rect x="54" y="149" width="112" height="15" fill="#9E3F17" rx="3" />
      <text x="110" y="160" textAnchor="middle" fill="#ffffff" fontSize="8" fontWeight="700">
        Akceptuję ofertę
      </text>
    </svg>
  );
}

function PremiumThumbnail({ width, height }: Pick<OfferThumbnailProps, "width" | "height">): JSX.Element {
  return (
    <svg
      className={styles.svg}
      width={width}
      height={height}
      viewBox="0 0 220 170"
      role="img"
      aria-label="Miniatura trybu Reprezentacyjna"
    >
      <rect width="220" height="170" fill="#14120D" />
      <rect width="220" height="4" fill="#C7541F" />
      <rect y="166" width="220" height="4" fill="#C7541F" />
      <rect x="20" y="22" width="34" height="22" fill="#ffffff" rx="2" />
      <text x="37" y="36" textAnchor="middle" fill="#14120D" fontSize="7" fontWeight="800">
        LOGO
      </text>
      <text x="20" y="78" fill="#ffffff" fontSize="22" fontWeight="700">
        ul. Królewska 22
      </text>
      <text x="20" y="108" fill="#C7541F" fontSize="26" fontWeight="800">
        48 000 zł
      </text>
      <line x1="20" y1="128" x2="200" y2="128" stroke="#C7541F" strokeWidth="1" opacity="0.7" />
      <text x="20" y="148" fill="#ffffff" fontSize="8" fontWeight="600">
        Data
      </text>
      <text x="20" y="160" fill="#BDB5A8" fontSize="8">
        25.06.2025
      </text>
      <text x="87" y="148" fill="#ffffff" fontSize="8" fontWeight="600">
        Gwarancja
      </text>
      <text x="87" y="160" fill="#BDB5A8" fontSize="8">
        24 miesiące
      </text>
      <text x="165" y="148" fill="#ffffff" fontSize="8" fontWeight="600">
        Termin
      </text>
      <text x="165" y="160" fill="#BDB5A8" fontSize="8">
        2-4 tyg.
      </text>
    </svg>
  );
}

function OfferThumbnailComponent({
  templateType,
  width,
  height,
}: OfferThumbnailProps): JSX.Element {
  if (templateType === "premium") return <PremiumThumbnail width={width} height={height} />;
  if (templateType === "explained") return <ExplainedThumbnail width={width} height={height} />;
  return <QuickThumbnail width={width} height={height} />;
}

export const OfferThumbnail = memo(
  OfferThumbnailComponent,
  (prev, next) =>
    prev.templateType === next.templateType &&
    prev.width === next.width &&
    prev.height === next.height,
);
